// App
import isNil from 'lodash/isNil';
import numeral from 'numeral';

// Constellation
import { Button, Text, Trigger } from '@zillow/constellation';

// Components
import ChangePriceModal from '../ChangePriceModal';

// Flux
import { useAppSelector } from '../flux/configureStore';

// Misc / utils
import { parser as comparablesParser } from '../utils/comparablesUtils';
import { pluralize } from '../utils/stringUtils';
import { compact, trimDecimal } from '../utils/numberUtils';
import HomeMarker from '../images/icons/homeMarker.inline.svg';
import * as S from './styles';

// Types
import type Comparables from '../models/Comparables';

type ZestimateType = { rentZestimate: number };
type MyPropertyCardPropTypes = {
  listing: Comparables & Partial<ZestimateType>;
  isHovered?: boolean;
  lockHover?: boolean;
};

const MAX_CHARS_PER_LINE = 21;

const MyPropertyCardComponent = ({ listing, isHovered, lockHover }: MyPropertyCardPropTypes) => {
  const longestAddress = useAppSelector((state) => comparablesParser.getLongestAddress(state.comparables.items));
  const priceFilter = useAppSelector((state) => state.filter.price);
  // prevent js error when setting custom price for addresses without a zestimate
  const myPropertyDisplayPrice =
    priceFilter || listing.rentZestimate ? compact(priceFilter || listing.rentZestimate) : null;
  const myPropertyPriceSqft =
    '$' + trimDecimal(numeral(myPropertyDisplayPrice).value() / numeral(listing.sqft).value(), 2);

  const listingPriceSqft = isNil(listing.pricePerSqft) ? '—' : `${myPropertyPriceSqft} per sq ft`;

  // props are supplied from Trigger's triggered
  const changePriceModal = (props: any, close: () => void) => <ChangePriceModal {...props} close={close} />;

  const listingBeds = isNil(listing.beds) ? '—' : comparablesParser.beds(listing.beds);
  const listingBaths = isNil(listing.baths) ? '—' : pluralize('bath', listing.baths, true);
  const listingSqft = isNil(listing.sqft) ? '—' : `${listing.sqft} sq ft`;

  return (
    <S.CardContainer isHovered={!!(isHovered || lockHover)}>
      <S.MyPropertyHeader>
        <S.MyPropertyBanner fontType="bodySmall" fontColor="white" as="strong">
          My property
        </S.MyPropertyBanner>
        <S.MyPropertyIconContainer>
          <S.MyPropertyIcon>
            <HomeMarker height="80" width="100" />
          </S.MyPropertyIcon>
        </S.MyPropertyIconContainer>
        <Text fontType="body" marginTop="xs">
          <strong>{`$${myPropertyDisplayPrice}/mo`}</strong>
        </Text>
        <Text fontType="body" marginBottom="xs">
          {listingPriceSqft}
        </Text>
      </S.MyPropertyHeader>
      <S.CardAddress height={longestAddress.length > MAX_CHARS_PER_LINE ? 143 : null}>
        {longestAddress.length > MAX_CHARS_PER_LINE ? (
          <Text fontType="bodySmall" marginBottom="xs" style={{ marginTop: '27px' }}>
            {listing.street}
          </Text>
        ) : (
          <Text fontType="bodySmall" marginY="xs">
            {listing.street}
          </Text>
        )}
        {/* @ts-ignore triggered={changePriceModal} */}
        <Trigger triggered={changePriceModal}>
          <Button size="sm" buttonType="primary" marginBottom="xs">
            Adjust price
          </Button>
        </Trigger>
      </S.CardAddress>
      <S.MyPropertyFactsContainer>
        <Text fontType="bodySmall" marginY="xs" as="strong">
          {listingBeds}
        </Text>
        <Text fontType="bodySmall" marginBottom="xs" as="strong">
          {listingBaths}
        </Text>
        <Text fontType="bodySmall" marginBottom="xs" as="strong">
          {listingSqft}
        </Text>
      </S.MyPropertyFactsContainer>
    </S.CardContainer>
  );
};

const MyPropertyCard = (props: MyPropertyCardPropTypes) => {
  const { listing } = props;
  if (!listing) {
    return null;
  }
  return <MyPropertyCardComponent {...props} />;
};

export default MyPropertyCard;
